import React, { useState } from 'react';
import {
  ArrowDownUp,
  BarChart3,
  TrendingUp,
  CalendarRange,
  MousePointerClick,
  Table as TableIcon,
} from 'lucide-react';
import { ChartGuideModal } from '../ui';
import { TourLauncher, useDashboardTour } from '../ui/tour';
import { EVALUATION_GROWTH_TOUR } from './evaluationGrowthTour';

interface EvaluationGrowthGuideProps {
  /** نام شعبه‌ی انتخاب‌شده برای شخصی‌سازی متن راهنما */
  branchName?: string;
  /** نام برند انتخاب‌شده برای شخصی‌سازی متن راهنما */
  brandName?: string;
  className?: string;
}

/**
 * راهنمای جامع «جدول عملکرد ماهانه ارزیابی هوشمند» (جدول رشد).
 * یک دکمه نمایش می‌دهد که با کلیک روی آن یک Modal باز می‌شود و توضیح می‌دهد
 * هر ستون چه داده‌ای است، نشان رشد چه معنایی دارد، چطور مرتب‌سازی و نمودار کار
 * می‌کند و یک سطر نمونه را خط‌به‌خط شرح می‌دهد.
 * این کامپوننت خودکفا است و state باز/بسته بودن Modal را خودش مدیریت می‌کند.
 */
const EvaluationGrowthGuide: React.FC<EvaluationGrowthGuideProps> = ({
  branchName,
  brandName,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { startTour } = useDashboardTour(EVALUATION_GROWTH_TOUR, {
    storageKey: 'tour:evaluation-growth',
  });

  const branchLabel = branchName ? `شعبه ${branchName}` : 'شعبه‌ی شما';
  const brandLabel = brandName ? `گروه ${brandName}` : 'گروه برند';

  return (
    <>
      {/* دکمه‌های مشترکِ راه‌اندازیِ تور (شروع آموزش + راهنمای کامل) */}
      <TourLauncher
        className={className}
        onStartTour={startTour}
        onOpenGuide={() => setIsOpen(true)}
        guideButtonTourId="eval-guide-btn"
      />

      <ChartGuideModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="راهنمای جدول عملکرد ماهانه ارزیابی هوشمند"
      >
        <div className="space-y-10 text-right leading-relaxed">
          {/* مقدمه */}
          <section>
            <p className="text-text-secondary">
              این جدول، مقدار شاخص‌های کلیدی{' '}
              <span className="font-semibold text-text-primary">
                ارزیابی و نظرسنجی
              </span>{' '}
              را برای {branchLabel} در هر ماه نشان می‌دهد و زیر مقدار چهار شاخص
              مهم،{' '}
              <span className="font-semibold text-text-primary">
                میزان رشد یا کاهش نسبت به ماه قبل
              </span>{' '}
              را هم اضافه می‌کند. هدف، دیدن سریعِ روند ماه‌به‌ماه و مقایسه‌ی هر
              شاخص با {brandLabel} است.
            </p>
          </section>

          {/* بخش ۱: دکمه‌های سرستون */}
          <GuideSection
            icon={<ArrowDownUp size={20} />}
            title="۱) دکمه‌های هر سرستون"
          >
            <p className="text-text-secondary mb-4">
              زیر عنوان هر ستون، یک یا دو دکمه وجود دارد که نمایش جدول را کنترل
              می‌کنند:
            </p>
            <ul className="space-y-3">
              <GuideItem label="دکمه «مرتب»">
                جدول را بر اساس همان ستون مرتب می‌کند. اولین کلیک، مرتب‌سازی{' '}
                <b>نزولی</b> (از بیشترین به کمترین) و کلیک دوباره،{' '}
                <b>صعودی</b> (از کمترین به بیشترین) می‌شود. فلش کنار دکمه، جهت فعلی
                مرتب‌سازی را نشان می‌دهد.
              </GuideItem>
              <GuideItem label="دکمه «نمودار»">
                برای ستون‌های عددی، نمودار آن شاخص را در پایین جدول باز می‌کند و
                روند {branchLabel} را با {brandLabel} مقایسه می‌کند. کلیک دوباره
                روی همان دکمه، نمودار را می‌بندد.
              </GuideItem>
            </ul>
            <div className="mt-4 flex items-start gap-2 text-sm text-text-secondary bg-status-info-bg border border-status-info rounded-lg p-3">
              <MousePointerClick size={18} className="text-status-info shrink-0 mt-0.5" />
              <span>
                روی موبایل و تبلت، جدول را با انگشت به چپ و راست بکشید تا ستون‌های
                بیشتری ببینید.
              </span>
            </div>
          </GuideSection>

          {/* بخش ۲: ستون‌های جدول */}
          <GuideSection
            icon={<TableIcon size={20} />}
            title="۲) معنی هر ستون جدول"
          >
            <p className="text-text-secondary mb-4">
              هر <b>سطر</b> یک ماه است و هر <b>ستون</b> یک شاخص. واحد هر شاخص هم
              کنار آن مشخص شده است (درصد یا تعداد):
            </p>
            <ul className="space-y-3">
              <ColumnRow label="ماه" unit="—">
                ماه و سال هر سطر (مثلاً «خرداد ۱۴۰۴»). سطرها به‌صورت پیش‌فرض از
                جدیدترین ماه به قدیمی‌ترین مرتب شده‌اند.
              </ColumnRow>
              <ColumnRow label="اشتراک‌گیری" unit="درصد" hasGrowth>
                درصد فاکتورهایی که اطلاعات تماس مشتری در آن‌ها ثبت شده است؛ هرچه
                بیشتر، یعنی شماره‌ی مشتریان بیشتری برای نظرسنجی جمع شده.
              </ColumnRow>
              <ColumnRow label="تعداد سفارش" unit="تعداد">
                تعداد کل فاکتورها (سفارش‌ها) ثبت‌شده در آن ماه. مبنای محاسبه‌ی
                بقیه‌ی نسبت‌هاست.
              </ColumnRow>
              <ColumnRow label="درصد نظرات مثبت" unit="درصد" hasGrowth>
                سهم نظرات راضی از کل نظرات ثبت‌شده در آن ماه.
              </ColumnRow>
              <ColumnRow label="مشارکت در نظرسنجی" unit="درصد" hasGrowth>
                درصد مشتریانی که به نظرسنجی پاسخ داده‌اند؛ نشان‌دهنده‌ی میزان
                درگیر شدن مشتری با فرایند ارزیابی.
              </ColumnRow>
              <ColumnRow label="نظرات راضی" unit="تعداد">
                تعداد نظرهای مثبت ثبت‌شده در آن ماه.
              </ColumnRow>
              <ColumnRow label="نظرات ناراضی" unit="تعداد">
                تعداد نظرهای منفی ثبت‌شده در آن ماه.
              </ColumnRow>
              <ColumnRow label="مجموع نظرات" unit="تعداد">
                مجموع کل نظرات (راضی + ناراضی) دریافت‌شده در آن ماه.
              </ColumnRow>
              <ColumnRow label="پیامک‌های ارسالی" unit="تعداد">
                تعداد پیامک‌های نظرسنجی ارسال‌شده برای مشتریان در آن ماه.
              </ColumnRow>
              <ColumnRow
                label="روزهای بدون شارژ پیامک"
                unit="تعداد"
                hasGrowth
              >
                تعداد روزهایی که به‌دلیل اتمام شارژ پنل، امکان ارسال پیامک نبوده
                است. <b>برخلاف بقیه‌ی شاخص‌ها، هرچه این عدد کمتر باشد بهتر است.</b>
              </ColumnRow>
            </ul>
          </GuideSection>

          {/* بخش ۳: نشان رشد + سطر نمونه */}
          <GuideSection
            icon={<TrendingUp size={20} />}
            title="۳) نشان رشد (زیر مقدار) چطور خوانده می‌شود؟"
          >
            <p className="text-text-secondary mb-4">
              زیر مقدارِ چهار شاخصِ کلیدی (اشتراک‌گیری، درصد نظرات مثبت، مشارکت در
              نظرسنجی و روزهای بدون شارژ پیامک)، یک نشانِ رنگی نمایش داده می‌شود که
              تغییر آن شاخص را <b>نسبت به ماه قبل</b> می‌سنجد.
            </p>

            {/* سلول نمونه‌ی آنوتیت‌شده */}
            <div className="bg-bg-secondary/50 rounded-xl p-5 border border-border-primary">
              <p className="text-sm font-semibold text-text-primary mb-4">
                یک سلول نمونه و معنای اعداد داخل آن:
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-6">
                {/* خود سلول نمونه (هم‌شکل با جدول واقعی) */}
                <div className="shrink-0">
                  <div className="flex flex-col items-center gap-1 rounded-xl border border-border-primary bg-bg-card px-6 py-4 shadow-md">
                    <span className="font-semibold text-lg text-text-primary" dir="ltr">
                      ۴۵.۲٪
                    </span>
                    <div
                      dir="ltr"
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-bold"
                      style={{
                        backgroundColor: 'rgba(5, 150, 105, 0.1)',
                        color: 'var(--status-success)',
                      }}
                    >
                      <span>↗</span>
                      <span>۸.۳٪</span>
                    </div>
                  </div>
                </div>

                {/* توضیح اجزای سلول */}
                <ul className="flex-1 space-y-3 w-full">
                  <AnnotationRow color="bg-status-info" title="مقدار شاخص (۴۵.۲٪)">
                    مقدار واقعی شاخص در همان ماه. اینجا یعنی ۴۵.۲٪ از فاکتورها
                    منجر به اشتراک‌گیری شده است.
                  </AnnotationRow>
                  <AnnotationRow color="bg-status-success" title="نشان سبز ↗ ۸.۳٪">
                    این شاخص نسبت به ماه قبل <b>۸.۳٪ رشد</b> داشته است. فلش رو به
                    بالا (↗) و رنگ سبز یعنی بهتر شده.
                  </AnnotationRow>
                  <AnnotationRow color="bg-status-error" title="نشان قرمز ↘">
                    اگر فلش رو به پایین (↘) و رنگ قرمز باشد، یعنی شاخص نسبت به ماه
                    قبل <b>کاهش</b> داشته است.
                  </AnnotationRow>
                  <AnnotationRow color="bg-text-tertiary" title="خط تیره (—)">
                    برای اولین ماه (که ماه قبلی برای مقایسه ندارد) نشان رشد نمایش
                    داده نمی‌شود.
                  </AnnotationRow>
                </ul>
              </div>

              <div className="mt-4 flex items-start gap-2 text-sm text-text-secondary bg-status-info-bg border border-status-info rounded-lg p-3">
                <MousePointerClick size={18} className="text-status-info shrink-0 mt-0.5" />
                <span>
                  موس را روی نشانِ رشد نگه دارید تا مقدار دقیق تغییر در یک
                  راهنمای کوچک نمایش داده شود.
                </span>
              </div>
            </div>
          </GuideSection>

          {/* بخش ۴: نمودار مقایسه‌ای */}
          <GuideSection
            icon={<BarChart3 size={20} />}
            title="۴) نمودار تحلیل مقایسه‌ای"
          >
            <p className="text-text-secondary">
              با کلیک روی دکمه‌ی «نمودار» هر ستون، نموداری در پایین جدول باز می‌شود
              که روند آن شاخص را در طول ماه‌ها نشان می‌دهد و {branchLabel} را با{' '}
              {brandLabel} مقایسه می‌کند. شاخص‌های درصدی به‌صورت{' '}
              <b>نمودار میله‌ای</b> و شاخص‌های تعدادی به‌صورت{' '}
              <b>نمودار خطی</b> رسم می‌شوند. خط (یا میله‌ی) مربوط به گروه برند،
              مبنای مقایسه است تا ببینید شعبه بالاتر یا پایین‌تر از میانگین گروه
              قرار دارد.
            </p>
          </GuideSection>

          {/* بخش ۵: بازه‌ی زمانی */}
          <GuideSection
            icon={<CalendarRange size={20} />}
            title="۵) بازه‌ی زمانی"
          >
            <p className="text-text-secondary">
              تعداد ماه‌های نمایش‌داده‌شده در جدول، از کنترلِ بازه‌ی زمانیِ بالای
              صفحه پیروی می‌کند: <b>۳ ماه</b>، <b>۶ ماه</b> یا <b>۱۲ ماه</b> اخیر.
              با تغییر این بازه، هم سطرهای جدول و هم نمودارها به‌روزرسانی می‌شوند.
            </p>
          </GuideSection>
        </div>
      </ChartGuideModal>
    </>
  );
};

/* ---------- اجزای کمکی داخلی ---------- */

const GuideSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ icon, title, children }) => (
  <section>
    <div className="flex items-center gap-2 mb-3">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-accent-purple/10 text-accent-purple shrink-0">
        {icon}
      </span>
      <h3 className="text-lg sm:text-xl font-semibold text-text-primary">
        {title}
      </h3>
    </div>
    {children}
  </section>
);

const GuideItem: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <li className="bg-bg-secondary/40 rounded-lg p-3 border border-border-primary">
    <span className="font-semibold text-text-primary">{label}: </span>
    <span className="text-text-secondary">{children}</span>
  </li>
);

const ColumnRow: React.FC<{
  label: string;
  unit: string;
  hasGrowth?: boolean;
  children: React.ReactNode;
}> = ({ label, unit, hasGrowth, children }) => (
  <li className="bg-bg-secondary/40 rounded-lg p-3 border border-border-primary">
    <div className="flex flex-wrap items-center gap-2 mb-1">
      <span className="font-semibold text-text-primary">{label}</span>
      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-bg-tertiary text-text-secondary border border-border-primary">
        {unit}
      </span>
      {hasGrowth && (
        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full intent-success-bg text-status-success border border-status-success/40">
          دارای نشان رشد
        </span>
      )}
    </div>
    <span className="text-text-secondary text-sm">{children}</span>
  </li>
);

const AnnotationRow: React.FC<{
  color: string;
  title: string;
  children: React.ReactNode;
}> = ({ color, title, children }) => (
  <li className="flex items-start gap-3">
    <span
      className={`${color} w-4 h-4 rounded-md shrink-0 mt-1 border border-border-primary`}
    />
    <div>
      <p className="font-semibold text-text-primary text-sm">{title}</p>
      <p className="text-text-secondary text-sm">{children}</p>
    </div>
  </li>
);

export default EvaluationGrowthGuide;
